/**
 * CollapsibleSection.tsx
 * ========================
 * Header bar that toggles its body. Accepts a "summary" slot rendered
 * to the right of the title — meant for active-filter chips and key
 * counts so users know what's inside without expanding.
 *
 * Designed for vertically-stacked dashboards where only one should be
 * open at a time. Open/close state is controlled by the parent so
 * sibling sections can coordinate.
 */
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';

interface Props {
  title:    string;
  icon?:    any;
  iconBg?:  string;
  iconColor?: string;
  summary?: React.ReactNode;
  open:     boolean;
  onToggle: () => void;
  children: React.ReactNode;
  /** Show a left-edge accent line when section has active filters */
  hasActiveFilters?: boolean;
}

const T = {
  bgCard:      '#ffffff',
  bgSidebar:   '#f5f7fa',
  textPrimary: '#0f1923',
  textMuted:   '#445566',
  brand:       '#1e3a6e',
  brandLight:  '#dde7f5',
  border:      '#c8d4e0',
};

const CollapsibleSection: React.FC<Props> = ({
  title, icon, iconBg, iconColor,
  summary, open, onToggle, children, hasActiveFilters,
}) => (
  <div style={{
    background: T.bgCard,
    border: `1px solid ${T.border}`,
    borderRadius: 10,
    marginBottom: 8,
    overflow: 'hidden',
    borderLeft: hasActiveFilters ? `3px solid ${T.brand}` : `1px solid ${T.border}`,
  }}>
    {/* Header bar — always visible */}
    <button
      onClick={onToggle}
      style={{
        width: '100%',
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 14px',
        background: open ? T.bgSidebar : 'transparent',
        border: 'none', cursor: 'pointer', textAlign: 'left',
        transition: 'background .15s',
      }}
      onMouseEnter={e => { if (!open) e.currentTarget.style.background = T.bgSidebar; }}
      onMouseLeave={e => { if (!open) e.currentTarget.style.background = 'transparent'; }}
    >
      <FontAwesomeIcon
        icon={open ? faChevronDown : faChevronRight}
        style={{ fontSize: 10, color: T.textMuted, width: 12 }}
      />
      {icon && (
        <div style={{
          width: 22, height: 22, borderRadius: 5,
          background: iconBg || T.brandLight,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <FontAwesomeIcon icon={icon} style={{ fontSize: 10, color: iconColor || T.brand }} />
        </div>
      )}
      <span style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 10, fontWeight: 700, letterSpacing: '.08em',
        textTransform: 'uppercase', color: T.textPrimary,
      }}>
        {title}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        {summary && (
          <div style={{
            fontSize: 11, color: T.textMuted,
            display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center',
          }}>
            {summary}
          </div>
        )}
      </div>
    </button>

    {/* Body */}
    {open && (
      <div style={{
        padding: '12px 14px',
        borderTop: `1px solid ${T.border}`,
        background: T.bgCard,
      }}>
        {children}
      </div>
    )}
  </div>
);

export default CollapsibleSection;